import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Languages } from "lucide-react";
import { formatPrice } from "@/lib/utils";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const cleanSiteName = (name) => {
  if (!name) return "";
  let cleaned = name.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return cleaned.replace(/Chanel News/gi, "Channel News");
};

const parseTranslation = (raw) => {
  if (!raw) return null;
  let val = String(raw).trim();
  if (!val) return null;
  const lower = val.toLowerCase();
  if (lower === "no" || lower === "n/a" || lower === "none") return null;
  val = val.replace(/\([^)]*\)/g, " ").replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();
  if (!val) return null;
  const numMatch = val.match(/(\d+)\s*[-]?\s*languages?/i);
  if (numMatch) return `${numMatch[1]} translations included`;
  const parts = val.split(/[,&/]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1) return `${parts.length} translations available`;
  return "Translation available";
};

export default function NewswireCard({ newswire }) {
  const [logoError, setLogoError] = useState(false);
  const ns = newswire;
  const price = ns.discount_price_usd ?? ns.price_usd;
  const keySites = (ns.key_sites || []).map(cleanSiteName).filter(Boolean);
  const displayCount = Math.min(3, keySites.length);
  const displaySites = keySites.slice(0, displayCount);
  const remaining = keySites.length - displayCount;
  const totalPickups = ns.media_outlets_count;
  const translationLabel = parseTranslation(ns.translation_included);
  const sectors = ns.derivedSectors || [];
  const region = ns.derivedRegion || "";
  const isProprietary = ns.is_proprietary !== false;

  return (
    <Link
      to="/dashboard/new-order"
      className="flex flex-col h-full bg-[rgba(255,255,255,0.58)] backdrop-blur-xl border border-[rgba(255,255,255,0.85)] rounded-2xl px-6 py-5 hover:-translate-y-0.5 transition-all duration-200 group/card relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(124,58,237,0.4)] focus-visible:ring-offset-2"
      style={{ boxShadow: "0 12px 32px rgba(90,60,160,0.08)" }}
    >
      {isProprietary ? (
        <img
          src="https://media.base44.com/images/public/6a460432963500ce22897197/28a667292_primaryicon4x.png"
          alt="Digital24"
          width={26}
          height={26}
          className="absolute top-3 right-3 w-[26px] h-[26px] object-contain z-10 select-none"
        />
      ) : (
        <span className="absolute top-3 right-3 text-[9px] font-medium tracking-wider px-2 py-0.5 rounded-full bg-transparent border border-[rgba(28,28,32,0.08)] text-[#8e8e96] z-10 select-none whitespace-nowrap">
          PREMIUM WIRE
        </span>
      )}

      <div className="flex items-center justify-center h-14 mb-4 max-w-full overflow-hidden">
        {ns.brand_logo_url && !logoError ? (
          <img
            src={ns.brand_logo_url}
            alt={ns.name}
            style={{ maxHeight: Math.min(56, 32 * (ns.logo_display_scale || 1)) }}
            className="max-w-full w-auto object-contain grayscale opacity-60 mix-blend-multiply group-hover/card:grayscale-0 group-hover/card:opacity-100 transition-all duration-300"
            onError={() => setLogoError(true)}
          />
        ) : (
          <span className="font-heading text-lg font-bold text-[#1c1c20] text-center">{ns.name}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-1.5 mb-4">
        {region && (
          <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-[rgba(28,28,32,0.04)] border border-[rgba(28,28,32,0.06)] text-[#6c6c74]">
            {region}
          </span>
        )}
        {sectors.map((s) => (
          <span key={s} className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-[rgba(124,58,237,0.1)] text-[#7c3aed]">
            {capitalize(s)}
          </span>
        ))}
      </div>

      <div className="mb-4 py-3 border-y border-[rgba(28,28,32,0.06)]">
        {keySites.length > 0 && (
          <div className="mb-3 pb-3 border-b border-[rgba(28,28,32,0.06)]">
            <div className="text-[10px] uppercase tracking-wider text-[#8e8e96] mb-1">Key pickups</div>
            <p className="text-sm text-[#55555c] leading-snug">
              {displaySites.join(", ")}
              {remaining > 0 && <span className="text-[#8e8e96]"> +{remaining} more</span>}
            </p>
          </div>
        )}
        <div>
          <div className="font-mono text-xl font-bold text-[#1c1c20]">{totalPickups ?? 0}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#8e8e96] mt-0.5">Total pickups</div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mt-auto">
        {translationLabel ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[rgba(139,92,246,0.09)] text-[#7c3aed]">
            <Languages className="w-3 h-3" />
            {translationLabel}
          </span>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-1 shrink-0">
          {price != null ? (
            <span className="font-heading text-base font-bold text-[#7c3aed] whitespace-nowrap">
              ${formatPrice(price)}
            </span>
          ) : null}
          <ArrowUpRight className="w-4 h-4 text-[#8e8e96] group-hover/card:text-[#7c3aed] transition-colors" />
        </div>
      </div>
    </Link>
  );
}